<script setup lang="ts">
/**
 * NoteHoverTooltip
 *
 * Blendet beim **Hovern** einer Note deren Tonhöhe als kleine Box am Mauszeiger ein – auf
 * **beiden** Vorschauen. Reine Anzeige: `pointer-events-none`, kein Fokus, kein Scrollen, keine
 * Emits; der Tooltip kann Hover, Klick und Rechtsklick also nie abfangen.
 *
 * Die Inhalts-Unterscheidung ist dieselbe wie im Rechtsklick-Menü und liegt in genau einem Helfer
 * ({@link ./notePitchLabel!notePitchLabel}): **Eingabe** = notierter Wert direkt aus der `NoteInfo`
 * (kein Request), **Ausgabe** (`preferSounding`) = ausschließlich der klingende Ton aus
 * `POST /api/note-grips`, weil die dort notierte Tonhöhe die Staff-Position des Knopfes ist. Den
 * Abruf samt Cache und Notenzuordnung kapselt {@link ./useHoveredSoundingNote!useHoveredSoundingNote}.
 *
 * Unterdrückung bei offenen Overlays macht der Parent, indem er `note` auf `null` setzt – siehe
 * `hoverTooltipSuppressed` in {@link ./noteHoverTooltip}.
 */
import {computed, ref} from 'vue';
import {useElementSize} from '@vueuse/core';

import type {NoteInfo} from '@/composables/musicXmlDom';

import {hoverTooltipPosition} from './noteHoverTooltip';
import {notePitchLabel} from './notePitchLabel';
import {useHoveredSoundingNote} from '@/composables/useHoveredSoundingNote';

const {
  note = null,
  x = null,
  y = null,
  preferSounding = false,
  musicXml = undefined,
} = defineProps<{
  /** Note unter dem Zeiger; `null` = kein Tooltip (auch der Weg, ihn zu unterdrücken). */
  note?: NoteInfo | null;
  /** Zeigerposition in Viewport-Koordinaten. */
  x?: number | null;
  y?: number | null;
  /** true auf der Griffschrift-Ausgabe: nur der klingende Ton ist dort aussagekräftig. */
  preferSounding?: boolean;
  /** Aktuelles MusicXML – nur für die Griff-Abfrage der Ausgabe. */
  musicXml?: string | null | undefined;
}>();

const tooltipRef = ref<HTMLElement | null>(null);
const {width: tooltipWidth, height: tooltipHeight} = useElementSize(tooltipRef);

const {soundingNotes, loading} = useHoveredSoundingNote(
    () => musicXml,
    () => note,
    () => preferSounding,
);

const label = computed(() => notePitchLabel({
  note,
  preferSounding,
  soundingNotes: soundingNotes.value,
  loading: loading.value,
}));

// oldValue-Pattern, weil der Computed ein Objekt liefert (VUE_RULES Regel 2): beim Verweilen auf
// derselben Stelle bleibt die Referenz gleich und das Style-Binding erzeugt keinen Patch.
const tooltipStyle = computed<{left: string; top: string}>((oldValue) => {
  if (null === x || null === y) return oldValue ?? {left: '0px', top: '0px'};
  const {left, top} = hoverTooltipPosition({
    x,
    y,
    width: tooltipWidth.value,
    height: tooltipHeight.value,
    viewportWidth: typeof window === 'undefined' ? 1024 : window.innerWidth,
    viewportHeight: typeof window === 'undefined' ? 768 : window.innerHeight,
  });
  const next = {left: `${left}px`, top: `${top}px`};
  if (oldValue && oldValue.left === next.left && oldValue.top === next.top) return oldValue;
  return next;
});
</script>

<template>
  <!-- z-40: unter den z-50-Backdrops der Dialoge/Menüs. aria-hidden, weil der Inhalt rein
       mausgebunden ist und für Screenreader keine erreichbare Information darstellt. -->
  <Transition
      enter-active-class="animate-fade animate-duration-150"
      leave-active-class="transition-opacity duration-100"
      leave-to-class="opacity-0"
  >
    <div
        v-if="label && x !== null && y !== null"
        ref="tooltipRef"
        aria-hidden="true"
        class="pointer-events-none fixed z-40 select-none whitespace-nowrap rounded-md bg-gray-900/90 px-2 py-1 text-xs font-semibold text-white shadow-lg"
        :style="tooltipStyle"
    >
      {{ label }}
    </div>
  </Transition>
</template>
